/**
 * sl-9qm.11: View-Scoped Search Types
 *
 * Shared types for search configuration and search results
 * used by both the SearchBar component and the backend search API.
 */
import { z } from 'zod';

/** Zod schema for SearchConfig validation (ViewConfig.settings.search) */
export const searchConfigSchema = z.object({
  enabled: z.boolean().default(true),
  searchableFields: z.array(z.string()).default([]),
  boost: z.record(z.string(), z.number()).default({}),
  debounce: z.number().int().min(0).max(2000).default(300),
  minChars: z.number().int().min(1).max(10).default(2),
  highlight: z.boolean().default(true),
  maxResults: z.number().int().min(1).max(100).default(10),
});

/** Search configuration stored in ViewConfig.settings.search */
export interface SearchConfig {
  enabled: boolean;
  searchableFields: string[];
  boost: Record<string, number>;
  debounce: number;
  minChars: number;
  highlight: boolean;
  maxResults: number;
}

/** A single search result from the backend */
export interface SearchResult {
  rowId: string;
  cells: Record<string, unknown>;
  score: number;
  highlights: Record<string, string> | null;
}

/** Complete search response from the backend API */
export interface SearchResponse {
  query: string;
  results: SearchResult[];
  totalMatches: number;
}

/** Default search configuration values */
export const DEFAULT_SEARCH_CONFIG: SearchConfig = {
  enabled: true,
  searchableFields: [],
  boost: {},
  debounce: 300,
  minChars: 2,
  highlight: true,
  maxResults: 10,
};
